import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { API_BASE_URL } from '../services/legistarApi';
import { Matter, Sponsor, MatterHistory, MatterAttachment } from '../types/legislation';
import { EventAction } from './event/ActionTimeline';
import MatterGroupCard from './event/MatterGroupCard';
import StandaloneEventItem from './event/StandaloneEventItem';

// Raw event item from the API
interface RawEventItem {
  EventItemId: number;
  EventItemGuid: string;
  EventItemAgendaSequence: number | null;
  EventItemTitle: string | null;
  EventItemAgendaNumber: string | null;
  EventItemMatterId: number | null;
  EventItemMatterGuid: string | null;
  EventItemMatterFile: string | null;
  EventItemMatterName: string | null;
  EventItemMatterType: string | null;
  EventItemMatterStatus: string | null;
  EventItemActionId: number | null;
  EventItemActionName: string | null;
  EventItemActionText: string | null;
  EventItemPassedFlag: number | null;
  EventItemPassedFlagName: string | null;
  EventItemTally: string | null;
  EventItemMover: string | null;
  EventItemSeconder: string | null;
  EventItemVersion: string | null;
  EventItemAgendaNote: string | null;
  EventItemMinutesNote: string | null;
}

// A group of event items sharing the same matter
interface MatterGroup {
  matterId: number;
  matter: Matter;
  sponsors: Sponsor[];
  histories: MatterHistory[];
  attachments: MatterAttachment[];
  actions: EventAction[];
  firstAgendaSeq: number; // for ordering
}

// A standalone item (no matter)
interface StandaloneItem {
  item: RawEventItem;
  agendaSeq: number;
}

type AgendaEntry =
  | { type: 'matter'; group: MatterGroup }
  | { type: 'standalone'; item: StandaloneItem };

// Per-matter bundle from the aggregated /events/:id/detail endpoint
interface MatterDetailBundle {
  Matter: Matter;
  Sponsors: Sponsor[];
  Histories: MatterHistory[];
  Attachments: MatterAttachment[];
}

// The event header info from the aggregated endpoint
interface EventInfo {
  EventId: number;
  EventBodyName: string | null;
  EventDate: string | null;
  EventTime: string | null;
  EventLocation: string | null;
  EventComment: string | null;
  EventAgendaFile: string | null;
  EventMinutesFile: string | null;
  EventVideoPath: string | null;
  EventInSiteURL: string | null;
  EventAgendaStatusName: string | null;
  EventMinutesStatusName: string | null;
}

// Shape of GET /events/:eventId/detail
interface EventDetailResponse {
  Event: EventInfo;
  Items: RawEventItem[];
  MatterDetails: MatterDetailBundle[];
}

const EventDetail: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const [entries, setEntries] = useState<AgendaEntry[]>([]);
  const [eventInfo, setEventInfo] = useState<EventInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);

        // 1. Fetch the whole event in one aggregated request (event + items +
        //    per-matter sponsors/histories/attachments). Collapses the former
        //    1 + 4*M fan-out to a single round trip (codex PF3).
        const res = await fetch(`${API_BASE_URL}/events/${eventId}/detail`);
        if (!res.ok) throw new Error('Failed to fetch event detail');
        const data: EventDetailResponse = await res.json();
        setEventInfo(data.Event || null);
        const items: RawEventItem[] = data.Items || [];

        // Index the per-matter bundles by MatterId for O(1) lookup
        const bundleById = new Map<number, MatterDetailBundle>();
        (data.MatterDetails || []).forEach((bundle) => {
          if (bundle.Matter && bundle.Matter.MatterId != null) {
            bundleById.set(bundle.Matter.MatterId, bundle);
          }
        });

        // 2. Group items by EventItemMatterId (unchanged grouping logic)
        const matterMap = new Map<number, RawEventItem[]>();
        const standalones: StandaloneItem[] = [];

        items.forEach((item) => {
          if (item.EventItemMatterId) {
            const existing = matterMap.get(item.EventItemMatterId) || [];
            existing.push(item);
            matterMap.set(item.EventItemMatterId, existing);
          } else {
            standalones.push({
              item,
              agendaSeq: item.EventItemAgendaSequence ?? 9999,
            });
          }
        });

        // 3. For each unique matter, read its details from the bundled payload
        const matterIds = Array.from(matterMap.keys());
        const matterGroups: MatterGroup[] = matterIds.map((matterId) => {
          const groupItems = matterMap.get(matterId)!;
          const bundle = bundleById.get(matterId);

          // Build actions from the group items
          const actions: EventAction[] = groupItems.map((gi) => ({
            EventItemId: gi.EventItemId,
            EventItemActionName: gi.EventItemActionName,
            EventItemPassedFlag: gi.EventItemPassedFlag,
            EventItemPassedFlagName: gi.EventItemPassedFlagName,
            EventItemTally: gi.EventItemTally,
            EventItemMover: gi.EventItemMover,
            EventItemSeconder: gi.EventItemSeconder,
            EventItemVersion: gi.EventItemVersion,
            EventItemAgendaSequence: gi.EventItemAgendaSequence,
          }));

          // Use earliest agenda sequence for ordering
          const firstAgendaSeq = Math.min(
            ...groupItems.map((gi) => gi.EventItemAgendaSequence ?? 9999)
          );

          // Fall back to a synthetic matter if it wasn't in the payload (unscraped)
          const resolvedMatter: Matter = bundle?.Matter || {
            MatterId: matterId,
            MatterGuid: groupItems[0].EventItemMatterGuid || '',
            MatterFile: groupItems[0].EventItemMatterFile || `Matter ${matterId}`,
            MatterName: groupItems[0].EventItemMatterName || '',
            MatterTitle: groupItems[0].EventItemMatterName || '',
            MatterTypeName: groupItems[0].EventItemMatterType || '',
            MatterStatusName: groupItems[0].EventItemMatterStatus || '',
            MatterBodyId: 0,
            MatterBodyName: '',
            MatterIntroDate: '',
            MatterAgendaDate: null,
            MatterPassedDate: null,
            MatterEnactmentDate: null,
            MatterEnactmentNumber: null,
            MatterVersion: '',
            MatterText1: null,
            MatterText2: null,
            MatterText3: null,
            MatterText4: null,
            MatterText5: null,
          };

          return {
            matterId,
            matter: resolvedMatter,
            sponsors: bundle?.Sponsors || [],
            histories: bundle?.Histories || [],
            attachments: bundle?.Attachments || [],
            actions,
            firstAgendaSeq,
          };
        });

        // 4. Interleave matter groups and standalone items by agenda sequence
        const allEntries: AgendaEntry[] = [
          ...matterGroups.map((g): AgendaEntry => ({ type: 'matter', group: g })),
          ...standalones.map((s): AgendaEntry => ({ type: 'standalone', item: s })),
        ];

        allEntries.sort((a, b) => {
          const seqA = a.type === 'matter' ? a.group.firstAgendaSeq : a.item.agendaSeq;
          const seqB = b.type === 'matter' ? b.group.firstAgendaSeq : b.item.agendaSeq;
          return seqA - seqB;
        });

        setEntries(allEntries);
      } catch (err) {
        setError('Failed to load event details. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (eventId) load();
  }, [eventId]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-nyc-blue mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading event details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">{error}</p>
      </div>
    );
  }

  const formatHeaderDate = (dateString: string | null): string => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const headerLinks: { label: string; href: string }[] = [];
  if (eventInfo?.EventAgendaFile) headerLinks.push({ label: 'Agenda', href: eventInfo.EventAgendaFile });
  if (eventInfo?.EventMinutesFile) headerLinks.push({ label: 'Minutes', href: eventInfo.EventMinutesFile });
  if (eventInfo?.EventVideoPath) headerLinks.push({ label: 'Watch video', href: eventInfo.EventVideoPath });
  if (eventInfo?.EventInSiteURL) headerLinks.push({ label: 'View on Legistar', href: eventInfo.EventInSiteURL });

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Hearing header: committee, when/where, and the agenda/minutes/video links */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h1 className="text-3xl font-bold text-nyc-blue mb-1">
          {eventInfo?.EventBodyName || 'Event Details'}
        </h1>
        {eventInfo?.EventDate && (
          <p className="text-lg text-gray-800">
            {formatHeaderDate(eventInfo.EventDate)}
            {eventInfo.EventTime && <span> &middot; {eventInfo.EventTime}</span>}
          </p>
        )}
        {eventInfo?.EventLocation && (
          <p className="text-gray-600">{eventInfo.EventLocation}</p>
        )}
        {eventInfo?.EventComment && (
          <p className="text-sm italic text-gray-500 mt-1">{eventInfo.EventComment}</p>
        )}
        {headerLinks.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4">
            {headerLinks.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 text-sm font-medium rounded-md bg-nyc-blue text-white hover:bg-blue-800 transition-colors"
              >
                {link.label}
              </a>
            ))}
          </div>
        )}
      </div>
      <div className="space-y-4">
        {entries.map((entry) => {
          if (entry.type === 'matter') {
            const g = entry.group;
            return (
              <MatterGroupCard
                key={g.matterId}
                matter={g.matter}
                sponsors={g.sponsors}
                histories={g.histories}
                attachments={g.attachments}
                actions={g.actions}
                eventId={eventId!}
              />
            );
          } else {
            return (
              <StandaloneEventItem
                key={entry.item.item.EventItemId}
                item={entry.item.item}
              />
            );
          }
        })}
      </div>
    </div>
  );
};

export default EventDetail;
